import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import type { TagSchedule } from '~/services/TagsService';

import TagScheduleCreate from './TagScheduleCreate';
import TagScheduleEdit from './TagScheduleEdit';
import TagSchedulesList from './TagSchedulesList';

interface Props {
  tagId?: string;
  schedules: Array<TagSchedule>;
  onSchedulesChange: (schedules: Array<TagSchedule>) => void;
}

export default function TagSchedules({ tagId, schedules, onSchedulesChange }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleToEdit, setScheduleToEdit] = useState<TagSchedule | null>(null);

  const handleSaveSchedule = (schedule: TagSchedule) => {
    const uniqueSchedules = Array.from(new Map([...schedules, schedule].map((item) => [item.id, item])).values());
    onSchedulesChange(uniqueSchedules);
    if (!tagId) {
      const alreadyExists = schedules.some((s) => s.id === schedule.id);
      enqueueSnackbar(`Schedule ${alreadyExists ? 'updated' : 'created'}. It will be saved when you save the tag.`, {
        variant: 'success',
      });
    }
    setScheduleDialogOpen(false);
    setScheduleToEdit(null);
  };

  const handleCreateClick = () => {
    setScheduleToEdit(null);
    setScheduleDialogOpen(true);
  };

  const handleEditClick = (schedule: TagSchedule) => {
    setScheduleToEdit(schedule);
    setScheduleDialogOpen(true);
  };

  const handleClose = () => {
    setScheduleDialogOpen(false);
    setScheduleToEdit(null);
  };

  return (
    <>
      <TagSchedulesList
        tagId={tagId}
        schedules={schedules}
        onSchedulesChange={onSchedulesChange}
        onEditClick={handleEditClick}
        onCreateClick={handleCreateClick}
      />
      {scheduleToEdit ? (
        <TagScheduleEdit
          open={scheduleDialogOpen}
          tagId={tagId}
          scheduleToEdit={scheduleToEdit}
          onClose={handleClose}
          onSave={handleSaveSchedule}
        />
      ) : (
        <TagScheduleCreate tagId={tagId} open={scheduleDialogOpen} onClose={handleClose} onSave={handleSaveSchedule} />
      )}
    </>
  );
}
